/**
 * VideoStreamQualityManager - Manages video stream quality events with suppression logic
 * 
 * This class handles video stream quality increase/decrease events and provides
 * suppression functionality during stream stabilization periods.
 */
class VideoStreamQualityManager {
  constructor() {
    this.stream_started = false;
    this.suppressing_enabled = false;
    this.stabilization_timer = null;
    this.current_level = null;
    this.current_score = null;
    
    // Event callbacks
    this.onSanitizedVideoStreamQualityIncrease = null;
    this.onSanitizedVideoStreamQualityDecrease = null;
  }

  /**
   * Sets the callback for sanitized video stream quality increase events
   * @param {Function} callback - Function to call with (context, level, score) parameters
   */
  setOnSanitizedVideoStreamQualityIncrease(callback) {
    this.onSanitizedVideoStreamQualityIncrease = callback;
  }

  /**
   * Sets the callback for sanitized video stream quality decrease events
   * @param {Function} callback - Function to call with (context, level, score) parameters
   */
  setOnSanitizedVideoStreamQualityDecrease(callback) {
    this.onSanitizedVideoStreamQualityDecrease = callback;
  }

  /**
   * Handles video stream quality increase events
   * @param {*} context - Context parameter (passthrough to callbacks)
   */
  VideoStreamQualityIncrease(context) {
    if (!this.stream_started) {
      console.log("VideoStreamQualityIncrease: dropping event because stream is not started");
      return;
    }

    // If suppressing is enabled, drop the increase event
    if (this.suppressing_enabled) {
        console.log("VideoStreamQualityIncrease: dropping event because suppressing is enabled");
      return;
    }

    // Emit the sanitized event
    if (this.onSanitizedVideoStreamQualityIncrease) {
      this.onSanitizedVideoStreamQualityIncrease(context, this.current_level, this.current_score);
    }
  }

  /**
   * Handles video stream quality decrease events
   * @param {*} context - Context parameter (passthrough to callbacks)
   */
  VideoStreamQualityDecrease(context) {
    if (!this.stream_started) {
        console.log("VideoStreamQualityDecrease: dropping event because stream is not started");
      return;
    }

    // Decrease events are always honored and passed through
    if (this.onSanitizedVideoStreamQualityDecrease) {
      this.onSanitizedVideoStreamQualityDecrease(context, this.current_level, this.current_score);
    }
  }

  /**
   * Handles video stream quality change requests
   * @param {*} context - Context parameter (passthrough to callbacks)
   * @param {number|null|undefined} level - Quality level (null/undefined treated as not set)
   * @param {number} score - Quality score
   */
  VideoStreamQualityChangeRequested(context, level, score) {
    // Handle null/undefined level - treat as not set and default to level 3
    if (level === null || level === undefined) {
      if (this.current_level === null) {
        // First call with null/undefined level - set to default level 3
        this.current_level = 3;
        this.current_score = score;
        this.VideoStreamQualityIncrease(context);
      }
      return;
    }

    // If level is the same as current, do nothing
    if (this.current_level === level) {
      this.current_score = score; // Update score even if level is same
      return;
    }

    // Update current values
    const previous_level = this.current_level;
    this.current_level = level;
    this.current_score = score;

    // Determine if this is an increase or decrease
    if (previous_level === null) {
      // First time setting a level - treat as increase
      this.VideoStreamQualityIncrease(context);
    } else if (level > previous_level) {
      // Level increased
      this.VideoStreamQualityIncrease(context);
    } else if (level < previous_level) {
      // Level decreased
      this.VideoStreamQualityDecrease(context);
    }
  }

  /**
   * Starts the video stream with stabilization period
   * @param {number} stabilization_secs - Number of seconds to suppress quality increases
   * @param {number|null|undefined} level - Initial quality level (null/undefined treated as not set)
   */
  StreamStarted(stabilization_secs, level = null) {
    // Reset suppressing_enabled to false initially
    this.suppressing_enabled = false;
    this.stream_started = true;

    // Set initial level if provided
    if (level !== null && level !== undefined) {
      this.current_level = level;
    }

    // Clear any existing timer
    if (this.stabilization_timer) {
      clearTimeout(this.stabilization_timer);
    }

    // Set timer to enable suppression after stabilization period
    this.stabilization_timer = setTimeout(() => {
      this.suppressing_enabled = true;
      console.log("VideoStreamQualityManager: flapping suppression enabled");
    }, stabilization_secs * 1000);
  }

  /**
   * Stops the video stream and cancels any timers
   */
  StreamStopped() {
    // Clear the stabilization timer
    if (this.stabilization_timer) {
      clearTimeout(this.stabilization_timer);
      this.stabilization_timer = null;
    }

    // Stop the stream and reset state
    this.stream_started = false;
    this.suppressing_enabled = false;
  }

  /**
   * Gets the current stream state
   * @returns {Object} Current state of the manager
   */
  getState() {
    return {
      stream_started: this.stream_started,
      suppressing_enabled: this.suppressing_enabled,
      has_timer: this.stabilization_timer !== null,
      current_level: this.current_level,
      current_score: this.current_score
    };
  }
}

export default VideoStreamQualityManager;
